import { Modal, Button } from "react-bootstrap";
import {
  ChevronLeft,
  ChevronRight,
  Trash,
  X,
  ArrowsFullscreen,
  ArrowsAngleContract,
  Eye,
} from "react-bootstrap-icons";
import { useState, useEffect, useRef } from "react";
import { MediaItem } from "../lib/get-file-type";
import { getCachedBlob } from "../lib/blob-cache-persistent";
import { useLazyFileLoad } from "../hooks";
import { useMediaQuery } from "react-responsive";

interface MediaModalProps {
  media: MediaItem[];
  currentIndex: number | null;
  isOpen: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDelete?: (index: number) => void;
  goToMessage?: (msgId: string) => void;
}

export const MediaModal: React.FC<MediaModalProps> = ({
  media,
  isOpen,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  onDelete,
  goToMessage,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const textRef = useRef<HTMLPreElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [textContent, setTextContent] = useState<string | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const currentItem = media[currentIndex ?? 0] || media[0];
  const showNavigation = media.length > 1;
  const isImage = currentItem?.type === "image";
  const isVideo = currentItem?.type === "video";
  const isText = (currentItem as any)?.type === "text";

  const [startY, setStartY] = useState<number | null>(null);
  const [startX, setStartX] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen || !currentItem) {
      setCurrentSrc(null);
      setTextContent(null);
      setLoading(false);
      return;
    }

    const loadMedia = async () => {
      try {
        setLoading(true);

        if (isText) {
          let blob: Blob;

          if (currentItem.url.startsWith("blob:")) {
            const response = await fetch(currentItem.url);
            blob = await response.blob();
          } else {
            blob = (await getCachedBlob(currentItem.url)).blob;
          }

          const text = await blob.text();
          setTextContent(text);
          setCurrentSrc(null);
        } else {
          if (currentItem.url.startsWith("blob:")) {
            setCurrentSrc(currentItem.url);
          } else {
            const blobUrl = (await getCachedBlob(currentItem.url)).url;
            setCurrentSrc(blobUrl);
          }
          setTextContent(null);
        }
      } catch (error) {
        console.error("Failed to load media:", error);
        setCurrentSrc(null);
        setTextContent("Ошибка загрузки файла");
      } finally {
        setLoading(false);
      }
    };

    loadMedia();
  }, [isOpen, currentIndex, currentItem?.url]);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    } else {
      const el = isImage
        ? imgRef.current
        : isVideo
          ? videoRef.current
          : textRef.current;
      if (el) {
        el.requestFullscreen().catch(console.error);
      }
    }
  };

  const mediaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = mediaContainerRef.current;
    if (!container) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (startY !== null && startX !== null) {
        e.preventDefault();
      }
    };

    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [startY, startX]);

  if (!isOpen || media.length === 0) {
    return null;
  }

  const controlButtonStyle = {
    color: "white",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    border: "none",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  };

  const navButtonStyle = {
    position: "absolute" as const,
    top: "50%",
    transform: "translateY(-50%)",
    color: "white",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
    cursor: "pointer",
    transition: "background-color 0.2s",
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      backdrop={true}
      keyboard={true}
      className="media-modal"
      style={{
        zIndex: 1060,
        backgroundColor: "rgba(0, 0, 0, 0.58)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "0px",
          right: "0px",
          display: "flex",
          flexDirection: "row" as const,
          gap: "0px",
          zIndex: 1051,
        }}
      >
        {
          !isMobile &&
          <Button
            variant="link"
            onClick={toggleFullscreen}
            style={controlButtonStyle}
            title={isFullscreen ? "Выход из полноэкранного режима" : "Полноэкранный режим"}
          >
            {isFullscreen ? (
              <ArrowsAngleContract size={14} />
            ) : (
              <ArrowsFullscreen size={14} />
            )}
          </Button>
        }

        {onDelete && (
          <Button
            variant="link"
            className="p-0"
            onClick={() => onDelete(currentIndex || 0)}
            style={controlButtonStyle}
            title="Удалить"
          >
            <Trash size={18} />
          </Button>
        )}

        {goToMessage && currentItem.msgId && (
          <Button
            variant="link"
            className="p-0"
            onClick={() => currentItem.msgId && goToMessage(currentItem.msgId)}
            style={controlButtonStyle}
            title="Показать в чате"
          >
            <Eye size={18} />
          </Button>
        )}

        <Button
          variant="link"
          className="p-0"
          onClick={onClose}
          style={controlButtonStyle}
          title="Закрыть"
        >
          <X size={18} />
        </Button>

      </div>

      {showNavigation && !isMobile && (
        <>
          <Button
            variant="link"
            onClick={onPrev}
            style={{ ...navButtonStyle, left: "16px" }}
            title="Предыдущее изображение"
          >
            <ChevronLeft size={20} />
          </Button>

          <Button
            variant="link"
            onClick={onNext}
            style={{ ...navButtonStyle, right: "16px" }}
            title="Следующее изображение"
          >
            <ChevronRight size={20} />
          </Button>
        </>
      )}

      {showNavigation && (
        <div
          style={{
            position: "absolute",
            bottom: "12px",
            width: "100%",
            textAlign: "center",
            color: "white",
            fontSize: "14px",
            zIndex: 1051,
          }}
        >
          {(currentIndex || 0) + 1} из {media.length}
        </div>
      )}

      <div
        ref={mediaContainerRef}
        onTouchStart={(e) => {
          if (e.touches.length === 1) {
            setStartY(e.touches[0].clientY);
            setStartX(e.touches[0].clientX);
          }
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          if (startY === null || startX === null || e.changedTouches.length === 0) return;

          const endY = e.changedTouches[0].clientY;
          const endX = e.changedTouches[0].clientX;

          const deltaY = startY - endY;
          const deltaX = startX - endX;

          const SWIPE_THRESHOLD = 50;

          if (Math.abs(deltaY) > SWIPE_THRESHOLD && Math.abs(deltaY) > Math.abs(deltaX)) {
            onClose();
          }
          else if (Math.abs(deltaX) > SWIPE_THRESHOLD && showNavigation) {
            if (deltaX > 0) {
              onNext();
            } else {
              onPrev();
            }
          }

          setStartY(null);
          setStartX(null);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          position: "relative",
          padding: 0,
          margin: 0,
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          touchAction: "none",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <div className="spinner-border" role="status" style={{ width: "3rem", height: "3rem", color: "white" }}>
              <span className="visually-hidden">Загрузка...</span>
            </div>
          </div>
        ) : textContent ? (
          <pre
            ref={textRef}
            onWheel={(e) => {
              const el = e.currentTarget;
              const isScrollable = el.scrollHeight > el.clientHeight;
              if (isScrollable) {
                e.stopPropagation();
              }
            }}
            className="scrollbar-custom-y"
            style={{
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "20px",
              width: "90vw",
              maxWidth: "800px",
              maxHeight: "80vh",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              margin: 0,
              boxSizing: "border-box",
              borderRadius: "12px",
            }}
          >
            {textContent}
          </pre>
        ) : currentItem?.type === "other" ? (
          <div
            style={{
              color: "white",
              textAlign: "center",
              padding: "40px",
              fontSize: "1.1rem",
              borderRadius: "12px",
              lineHeight: 1.5,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            }}
          >
            Этот тип файла нельзя просмотреть в приложении.
            <br />
            Скачайте файл, чтобы открыть его на вашем устройстве.
          </div>
        ) : currentSrc ? (
          currentItem?.type === "image" ? (
            <img
              ref={imgRef}
              src={currentSrc}
              alt=""
              style={{
                maxWidth: "100%",
                maxHeight: isMobile ? "100%" : "90%",
                objectFit: "contain",
                visibility: loading ? "hidden" : "visible",
              }}
            />
          ) : (
            <video
              ref={videoRef}
              src={currentSrc}
              controls
              style={{
                maxWidth: "100%",
                maxHeight: isMobile ? "100%" : "90%",
                outline: "none",
                visibility: loading ? "hidden" : "visible",
              }}
            />
          )
        ) : (
          <div style={{ color: "red", textAlign: "center", padding: "20px" }}>
            Ошибка загрузки файла
          </div>
        )}
      </div>
    </Modal>
  );
};